import { Close } from '@fountain/fountain-ui-components';
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import {
  CatalogField,
  CatalogState,
  ConditionDraft,
  ConditionError,
  ConditionLogic,
  emptyCondition,
  fieldFor,
  isUnary,
  mintConditionKey,
  validateCondition,
  withCompareTo,
  withDataKey,
} from './conditionForm';
import messages from './messages';
import { useStyles } from './styles';

export interface ConditionBuilderProps {
  conditions: ConditionDraft[];
  catalog: CatalogState;
  /** Errors are only surfaced once the admin has tried to save. */
  showErrors: boolean;
  onChange: (conditions: ConditionDraft[]) => void;
  /**
   * How this level's conditions combine. Flat across the level — one operator
   * for the whole set, not a nested expression (AXHE-3930).
   */
  logic: ConditionLogic;
  onLogicChange: (logic: ConditionLogic) => void;
  /** Retries the catalog fetch; the builder is unusable without it. */
  onRetryCatalog: () => void;
  /** Distinguishes one level's rows from another's in the DOM. */
  idPrefix: string;
}

/** Which helper text a row's error maps to, so the message lives in one place. */
const ERROR_MESSAGES: Record<ConditionError, keyof typeof messages> = {
  no_data_key: 'conditionNeedsField',
  unknown_data_key: 'conditionFieldUnavailable',
  no_operator: 'conditionNeedsOperator',
  unsupported_operator: 'conditionOperatorUnavailable',
  no_value: 'conditionNeedsValue',
  no_compare_key: 'conditionNeedsCompareKey',
};

/**
 * What each server operator token reads as (AXHE-4473).
 *
 * The catalog sends `Approvals::ValueType::OPERATORS` verbatim — `greater_than`,
 * `not_equals` — which is a wire format, not something to put in front of an
 * admin. Mapped rather than transformed: `not_equals` is "does not equal", which
 * no amount of underscore-splitting produces.
 *
 * Deliberately a partial map with a fallback to the token. A type gaining an
 * operator server-side is a one-line change there, and the alternative to
 * degrading is an option that renders blank — unpickable, and indistinguishable
 * from the field having no operators at all.
 */
const OPERATOR_MESSAGES: Record<string, keyof typeof messages> = {
  equals: 'operatorEquals',
  not_equals: 'operatorNotEquals',
  contains: 'operatorContains',
  greater_than: 'operatorGreaterThan',
  less_than: 'operatorLessThan',
  is_present: 'operatorIsPresent',
};

const LOGIC_MESSAGES: Record<ConditionLogic, keyof typeof messages> = {
  and: 'conditionLogicAnd',
  or: 'conditionLogicOr',
};

/**
 * Which keyboard and picker the browser gives a typed literal.
 *
 * Everything not in the vocabulary lands on text, which is also where
 * `ValueType.normalize` puts an unrecognised type — so a field the server
 * degraded to string is not offered a numeric spinner here.
 */
const valueInputType = (type?: string | null) => {
  if (type === 'number') return 'number';
  if (type === 'date') return 'date';
  return 'text';
};

/**
 * Whether the value is chosen rather than typed.
 *
 * A boolean has exactly two, so a text box is the wrong control twice over: it
 * accepts input the field cannot hold, and it gives no clue which spelling
 * counts. `ValueType.coerce_boolean` takes `true/false/yes/no/1/0` and returns
 * nil for anything else — and a nil operand makes the comparison false, so
 * "Requires relocation equals Y" would save cleanly and then quietly never
 * gate anything.
 */
const isPickList = (field?: CatalogField) =>
  field?.type === 'boolean' || Boolean(field?.allowed_values?.length);

/**
 * The conditions deciding whether a level applies (AXHE-3929).
 *
 * A condition compares a field either to a literal or to **another field**, and
 * the second mode is the one that needed solving: comparing arbitrary fields is
 * incomprehensible, so the compare-with list is restricted to fields of the same
 * type and never contains the field being configured. That restriction comes
 * from the server (`comparable_keys`) rather than being re-derived here — see
 * `conditionForm.ts`.
 *
 * Operators are likewise type-derived: the catalog says which ones this field's
 * type allows, so the builder cannot offer `contains` on a number and then have
 * the save rejected.
 *
 * Nothing is editable until the catalog is `ready`. An empty field picker with
 * no explanation reads as "there is nothing to gate on here", which is the one
 * thing a failed fetch does not mean — and on an existing rule it would render
 * live conditions as blank selects on a compliance surface. Same reasoning as
 * the disabled compare-with toggle one level down: say why, rather than leave a
 * dead control.
 */
export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  conditions,
  catalog,
  showErrors,
  onChange,
  logic,
  onLogicChange,
  onRetryCatalog,
  idPrefix,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const update = (key: string, next: ConditionDraft) =>
    onChange(conditions.map(row => (row.key === key ? next : row)));

  const operatorLabel = (operator: string) => {
    const message = OPERATOR_MESSAGES[operator];
    return message ? intl.formatMessage(messages[message]) : operator;
  };

  const logicLabel = (value: ConditionLogic) =>
    intl.formatMessage(messages[LOGIC_MESSAGES[value]]);

  // Hoisted out of the row loop: it is one value per render, and computing it at
  // each connector let the click and its accessible name drift apart.
  const otherLogic: ConditionLogic = logic === 'and' ? 'or' : 'and';

  /**
   * The options behind a picked value.
   *
   * Booleans are stored as the strings `ValueType.coerce_boolean` reads, and
   * shown as Yes/No — an admin gating on a checkbox question is asking whether
   * it was ticked, not what its serialised form is.
   *
   * A stored value that is not among them is carried in as its own option. This
   * builder wrote a *free text* box for booleans until AXHE-4473, and
   * `coerce_boolean` accepts `yes/no/1/0/t/f` too — so those conditions exist,
   * are valid, and evaluate correctly. Without this the select would find no
   * matching option and render blank, while `validateCondition` passed the row
   * for being non-empty: a live gate that reads as unconfigured and flags
   * nothing. The same holds for an `allowed_values` list the account has since
   * edited.
   */
  const valueOptions = (
    field?: CatalogField,
    stored?: string,
  ): { value: string; label?: string }[] => {
    const options =
      field?.type === 'boolean'
        ? [
            { value: 'true', label: intl.formatMessage(globalMessages.yes) },
            { value: 'false', label: intl.formatMessage(globalMessages.no) },
          ]
        : (field?.allowed_values ?? []).map(allowed => ({ value: allowed }));

    if (stored && !options.some(option => option.value === stored)) {
      return [...options, { value: stored }];
    }
    return options;
  };

  const heading = (
    <Typography variant="subtitle2">
      <FormattedMessage {...messages.conditionsLabel} />
    </Typography>
  );

  if (catalog.status === 'loading') {
    return (
      <div className={styles.conditions}>
        {heading}
        <div className={styles.conditionsPlaceholder}>
          <CircularProgress
            size={20}
            aria-label={intl.formatMessage(messages.conditionsLoading)}
          />
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage {...messages.conditionsLoading} />
          </Typography>
        </div>
      </div>
    );
  }

  if (catalog.status === 'error') {
    return (
      <div className={styles.conditions}>
        {heading}
        {/* The rest of the panel still works — a level's approvers can be edited
            and saved, and `toPayload` re-emits these conditions as they were.
            Only the builder is unavailable, and it says so with a way back. */}
        <div className={styles.error} role="alert">
          <Typography variant="body1">
            <FormattedMessage {...messages.conditionsLoadError} />
          </Typography>
          <Button variant="outlined" onClick={onRetryCatalog}>
            <FormattedMessage {...messages.retry} />
          </Button>
        </div>
      </div>
    );
  }

  const { catalog: fieldCatalog } = catalog;

  return (
    <div className={styles.conditions}>
      {heading}

      {conditions.map((condition, index) => {
        const field = fieldFor(fieldCatalog, condition.dataKeyName);
        const error = showErrors
          ? validateCondition(condition, fieldCatalog)
          : undefined;
        // Only the fields the server says are comparable with this one. Empty is
        // a real answer — a lone date field has nothing to compare against.
        const comparable = field?.comparable_keys ?? [];
        const unary = isUnary(fieldCatalog, condition.operator);

        return (
          <React.Fragment key={condition.key}>
            {/* Between adjacent rows only. Every pill in the level shows the
                same word and toggles the same flag, because the level's
                conditions share one operator — that flatness is the deliberate
                simplification in AXHE-3930, not an omission. */}
            {index > 0 && (
              <Chip
                className={styles.connector}
                size="small"
                variant="outlined"
                clickable
                label={logicLabel(logic)}
                onClick={() => onLogicChange(otherLogic)}
                aria-label={intl.formatMessage(messages.conditionLogicToggle, {
                  current: logicLabel(logic),
                  other: logicLabel(otherLogic),
                })}
              />
            )}

            <div className={styles.conditionRow} data-testid="condition-row">
              <div className={styles.conditionControls}>
                <TextField
                  id={`${idPrefix}-field-${condition.key}`}
                  className={styles.conditionField}
                  label={intl.formatMessage(messages.conditionFieldLabel)}
                  value={condition.dataKeyName}
                  onChange={event =>
                    update(
                      condition.key,
                      withDataKey(condition, event.target.value, fieldCatalog),
                    )
                  }
                  error={error === 'no_data_key' || error === 'unknown_data_key'}
                  helperText={
                    error === 'no_data_key' || error === 'unknown_data_key'
                      ? intl.formatMessage(messages[ERROR_MESSAGES[error]])
                      : undefined
                  }
                  select
                >
                  {fieldCatalog.fields.map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label ?? option.key}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Operators come from the chosen field's type, so this stays
                    empty until a field is picked rather than offering every
                    operator in the vocabulary. */}
                <TextField
                  id={`${idPrefix}-operator-${condition.key}`}
                  className={styles.conditionOperator}
                  label={intl.formatMessage(messages.conditionOperatorLabel)}
                  value={condition.operator}
                  onChange={event =>
                    update(condition.key, {
                      ...condition,
                      operator: event.target.value,
                    })
                  }
                  error={error === 'no_operator' || error === 'unsupported_operator'}
                  helperText={
                    error === 'no_operator' || error === 'unsupported_operator'
                      ? intl.formatMessage(messages[ERROR_MESSAGES[error]])
                      : undefined
                  }
                  disabled={!field}
                  select
                >
                  {(field?.operators ?? []).map(operator => (
                    <MenuItem key={operator} value={operator}>
                      {operatorLabel(operator)}
                    </MenuItem>
                  ))}
                </TextField>

                {!unary && condition.compareTo === 'dataKey' && (
                  <TextField
                    id={`${idPrefix}-compare-${condition.key}`}
                    className={styles.conditionValue}
                    label={intl.formatMessage(messages.conditionCompareKeyLabel)}
                    value={condition.compareKey}
                    onChange={event =>
                      update(condition.key, {
                        ...condition,
                        compareKey: event.target.value,
                      })
                    }
                    error={error === 'no_compare_key'}
                    helperText={
                      error === 'no_compare_key'
                        ? intl.formatMessage(messages.conditionNeedsCompareKey)
                        : undefined
                    }
                    select
                  >
                    {comparable.map(key => (
                      <MenuItem key={key} value={key}>
                        {fieldFor(fieldCatalog, key)?.label ?? key}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {!unary && condition.compareTo === 'value' && (
                  <TextField
                    id={`${idPrefix}-value-${condition.key}`}
                    className={styles.conditionValue}
                    label={intl.formatMessage(messages.conditionValueLabel)}
                    value={condition.value}
                    onChange={event =>
                      update(condition.key, {
                        ...condition,
                        value: event.target.value,
                      })
                    }
                    error={error === 'no_value'}
                    helperText={
                      error === 'no_value'
                        ? intl.formatMessage(messages.conditionNeedsValue)
                        : undefined
                    }
                    // Held until a field is chosen, for the same reason the
                    // operator picker is: the type decides what a legal value
                    // even looks like, and `withDataKey` clears anything typed
                    // before then — so an editable box here only invites work it
                    // is about to discard.
                    disabled={!field}
                    // The input follows the field's type, so a date field gets a
                    // date picker and a number field a numeric input rather than
                    // everything being free text.
                    type={valueInputType(field?.type)}
                    InputLabelProps={
                      field?.type === 'date' ? { shrink: true } : undefined
                    }
                    select={isPickList(field)}
                  >
                    {valueOptions(field, condition.value).map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label ?? option.value}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {/* An icon, not a `×` text node: a glyph has no icon box, so the
                    button measured 17px square — under the minimum pointer
                    target, and it could only be aligned by guessing at the
                    input's own top margin. */}
                <IconButton
                  className={styles.conditionRemove}
                  size="small"
                  aria-label={`${intl.formatMessage(messages.removeCondition)} ${condition.key}`}
                  onClick={() =>
                    onChange(conditions.filter(row => row.key !== condition.key))
                  }
                >
                  <Close className={styles.conditionRemoveIcon} />
                </IconButton>
              </div>

              {/* Under the value it replaces, rather than above the row: it
                  changes what the right-hand side *is*, so it reads as a
                  modifier of that control. A unary operator tests for
                  existence, so there is no right-hand side and the checkbox
                  would be meaningless. */}
              {!unary && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={condition.compareTo === 'dataKey'}
                      disabled={!field || comparable.length === 0}
                      onChange={event =>
                        update(
                          condition.key,
                          withCompareTo(
                            condition,
                            event.target.checked ? 'dataKey' : 'value',
                          ),
                        )
                      }
                      inputProps={{
                        'aria-label': intl.formatMessage(
                          messages.compareToFieldToggle,
                        ),
                      }}
                    />
                  }
                  label={intl.formatMessage(messages.compareToFieldToggle)}
                />
              )}

              {/* Says why the checkbox is unavailable rather than leaving a
                  disabled control with no explanation.

                  `body2`, not `subtitle1`: this theme sets subtitle1 to 24px
                  book, so a sentence explaining a disabled checkbox rendered
                  larger than the panel's own title and was the loudest thing in
                  a row it only qualifies. */}
              {!unary && field && comparable.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  <FormattedMessage {...messages.noComparableFields} />
                </Typography>
              )}
            </div>
          </React.Fragment>
        );
      })}

      <Button
        onClick={() =>
          onChange([
            ...conditions,
            emptyCondition(mintConditionKey(idPrefix, conditions)),
          ])
        }
      >
        <FormattedMessage {...messages.addCondition} />
      </Button>
    </div>
  );
};
